import { FormikValues, useFormik } from "formik";
import { FC, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useSelector } from "react-redux";
import { authTriggerActions } from "../../backend/store/reducers/authReducer";
import { Layout } from "../../components/LoginPage/Layout";
import { emailRegex } from "../../regex";
import { useTranslation } from "../../hooks/useTranslation";
import { getDefaultLandingPage } from "../../utils/permissions";
import { isPublicNext } from "../../next/public/mode";
import { takeWantedPage } from "../../utils/wantedPage";

const login = {
  email: "",
  password: "",
  remember: 0,
};

type Formik<T extends FormikValues> = ReturnType<typeof useFormik<T>>;
export type LoginFormik = Formik<typeof login>;
export const LoginPage: FC = () => {
  const { p, i18n } = useTranslation("first_page");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isUser = localStorage.getItem("user");
  const storedUser = useSelector((state: any) => state.authStore.user);
  const user = useMemo(() => storedUser, [storedUser]);

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .required(p("email_is_required"))
      .matches(emailRegex, p("invalid_email")),
    password: yup
      .string()
      // P2.6 (new look): an older, shorter password may still try to sign in; the length rule belongs to setting a new one.
      .required(p("password_is_required"))
      .test("min8", p("min_8_characters_length"), (v) => isPublicNext() || (v || "").length >= 8),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: 0,
    },
    validationSchema,
    onSubmit: (data) => {
      dispatch(authTriggerActions.loginUserTriggerAC({ ...data, language: i18n.language }));
    },
  });

  const isDisabled = useMemo(() => {
    return (
      !formik.values.email ||
      !formik.values.password ||
      !!formik.errors.email ||
      !!formik.errors.password
    );
  }, [formik]);

  useEffect(() => {
    // P3.3 (new look): a signed-in person who opens a sign-in link is asked first (the sign-in page shows the question).
    if (isPublicNext() && new URLSearchParams(window.location.search).get("token")) return;
    if (isUser && user) {
      if (user.isApproved) {
        // P2.5 (new look): back to the page that was wanted, if it is an allowed in-app one; otherwise the usual landing page.
        navigate((isPublicNext() ? takeWantedPage() : null) ?? getDefaultLandingPage(user));
      } else {
        navigate("/unAuthPage");
      }
    }
  }, [isUser, user, navigate])

  return <Layout formik={formik} isDisabled={isDisabled} />;
};
